import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Spin } from 'antd'; // ✅ Restored explicit Ant Design UI modules
import { NotFound } from 'common/components';
import { getOrderStatusDetails } from 'api/nach'; // ✅ Added your API hook dependency

const CustomText = ({ title, value }: any) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px' }}>
      <span style={{ fontWeight: '600', fontSize: '16px' }}>{title}</span>
      <span style={{ fontSize: '16px', fontWeight: '400' }}>{value} </span>
    </div>
  );
};

function NachPaymentSummary() {
  const location = useLocation();
  const initialState = location.state as any;

  // ✅ LOCAL STATE MANAGEMENT: Allows the summary page to update live without forcing a full page reload
  const [currentStatus, setCurrentStatus] = useState<string>(initialState?.state || 'PENDING');
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [transactionId, setTransactionId] = useState<string>(initialState?.transactionId || 'N/A');

  if (!initialState) {
    return <NotFound />;
  }

  // ✅ RE-VERIFICATION TRIGGER: Hits the backend to check if a pending mandate has transitioned
  const handleRefreshStatus = () => {
    setIsVerifying(true);

    getOrderStatusDetails({
      orderId: initialState.merchantOrderId,
      loggedInUserId: 0,
      mandateId: initialState.mandateId,
    })
      .then((res) => {
        console.log('Summary page manual refresh checkpoint:', res);
        if (res.status === 'SUCCESS' && res.data) {
          // Update local status states based on the fresh payload data matrix
          if (res.data.state === 'COMPLETED' || res.data.state === 'SUCCESS') {
            setCurrentStatus('COMPLETED');
          } else if (res.data.state === 'FAILED') {
            setCurrentStatus('FAILED');
          } else {
            setCurrentStatus('PENDING');
          }

          // Capture the transaction tracking ID if it finally populated
          if (res.data.paymentDetails?.[0]?.transactionId) {
            setTransactionId(res.data.paymentDetails[0].transactionId);
          }
        }
      })
      .catch((err) => {
        console.error('Failed to manually update transaction details on receipt frame:', err);
      })
      .finally(() => {
        setIsVerifying(false);
      });
  };

  // Helper styling configuration to change badging colors dynamically
  const getStatusStyle = () => {
    if (currentStatus === 'COMPLETED' || currentStatus === 'SUCCESS') return { color: '#52c41a', fontWeight: '700' };
    if (currentStatus === 'FAILED') return { color: '#ff4d4f', fontWeight: '700' };
    return { color: '#faad14', fontWeight: '700' }; // Amber/Yellow for PENDING
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
      <Spin spinning={isVerifying} tip="Verifying latest payment status...">
        <div style={{ width: '600px', padding: '40px', backgroundColor: 'white', marginBottom: '20px', marginTop: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>

          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 20px', alignItems: 'center' }}>
            <img src="/logo.png" alt="logo" width={150} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: '2px', marginBottom: '15px' }}>
            <p style={{ fontWeight: '500', wordSpacing: '2px', letterSpacing: '1px', fontSize: '20px', marginBottom: '5px' }}>
              Autopay eMandate Summary
            </p>
            <span style={{ fontWeight: '700', width: '100%', textAlign: 'center', backgroundColor: '#ccc', fontSize: '15px', padding: '10px' }}>
              Mandate Summary
            </span>
          </div>

          <div>
            {/* Dynamic Status Row */}
            <CustomText
              title="Status"
              value={<span style={getStatusStyle()}>{currentStatus}</span>}
            />

            {initialState.mandateId && <CustomText title="Mandate Request ID" value={initialState.mandateId} />}
            {initialState.merchantOrderId && <CustomText title="Merchant Order ID" value={initialState.merchantOrderId} />}
            {initialState.amount && <CustomText title="Mandate Amount" value={'₹ ' + initialState.amount / 100} />}
            {initialState.orderId && <CustomText title="Order ID" value={initialState.orderId} />}
            <CustomText title="Transaction ID" value={transactionId} />
          </div>

          {/* ✅ DYNAMIC INTERACTION LAYER: Renders ONLY when the mandate is still processing/PENDING */}
          {currentStatus === 'PENDING' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '30px', borderTop: '1px dashed #e8e8e8', paddingTop: '20px' }}>
              <p style={{ color: '#fc4903', fontWeight: '500', fontSize: '14px', marginBottom: '15px', textAlign: 'center' }}>
                Your auto-pay configuration verification is still running.<br />
                Click below to check for real-time validation updates.
              </p>
              <Button
                type="primary"
                size="large"
                loading={isVerifying}
                onClick={handleRefreshStatus}
                style={{ background: '#fc4903', borderColor: '#fc4903', fontWeight: '600', width: '200px', borderRadius: '4px' }}
              >
                Get Status
              </Button>
            </div>
          )}

        </div>
      </Spin>
    </div>
  );
}

export default NachPaymentSummary;